/**
 * Sarathi — pricing tiles + Razorpay checkout entry.
 *
 * Design tokens from sarathi/index.html:
 *   --saffron  #c5460e  --saffron-dark #9a3306
 *   --cream    #fffaeb  --cream-2      #f7eccd
 *   --ink      #0d0a06  --ash          #5a554d
 *   --rule     #d4c8a0
 *   Font: Mukta (700) + Crimson Pro (serif display)
 *   Base size: 22px (AAA elder-friendly)
 */
import { PRICING, openCheckout } from '../chassis/razorpay.ts';

const TPL = /* html */ `
<style>
:host {
  display: block;
  margin: 48px 0 32px;
  font-size: 22px; /* match sarathi AAA base */
}
.shell {
  border: 2px solid #d4c8a0;
  background: linear-gradient(180deg, #fffef5 0%, #fffaeb 100%);
  padding: 36px 28px 28px;
  border-radius: 8px;
  position: relative;
}
.head {
  text-align: center;
  margin-bottom: 28px;
}
.eyebrow {
  font-family: 'Mukta', sans-serif;
  font-size: 14px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #9a3306;
  font-weight: 700;
}
h3 {
  font-family: 'Crimson Pro', Georgia, serif;
  font-size: 34px;
  font-style: italic;
  font-weight: 700;
  margin: 8px 0 6px;
  color: #0d0a06;
  line-height: 1.1;
}
.tagline {
  font-family: 'Mukta', sans-serif;
  font-size: 18px;
  color: #5a554d;
  line-height: 1.4;
}
.tiers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 16px;
}
.tier {
  background: #fff;
  border: 2px solid #d4c8a0;
  border-radius: 8px;
  padding: 24px 18px;
  display: grid;
  gap: 10px;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;
}
.tier:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(197,70,14,.12); }
.tier.recommended { border-color: #c5460e; }
.tier.recommended::before {
  content: 'recommended';
  display: block;
  font-family: 'Mukta', sans-serif;
  font-size: 12px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #fff;
  background: #c5460e;
  border-radius: 4px 4px 0 0;
  padding: 4px 10px;
  margin: -24px -18px 10px;
  text-align: center;
}
.tier .label {
  font-family: 'Mukta', sans-serif;
  font-size: 14px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #5a554d;
  font-weight: 600;
}
.tier .price {
  font-family: 'Crimson Pro', Georgia, serif;
  font-size: 38px;
  font-style: italic;
  color: #9a3306;
  font-weight: 700;
  line-height: 1;
}
.tier .per {
  font-family: 'Mukta', sans-serif;
  font-size: 14px;
  color: #5a554d;
  letter-spacing: 0.1em;
}
.tier .desc {
  font-family: 'Mukta', sans-serif;
  font-size: 16px;
  color: #2a2418;
  line-height: 1.45;
}
.tier button {
  appearance: none;
  font-family: 'Mukta', sans-serif;
  font-size: 18px;
  font-weight: 700;
  padding: 14px 12px;
  min-height: 56px; /* AAA tap target */
  background: #0d0a06;
  color: #fffaeb;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 8px;
  transition: background 100ms;
}
.tier button:hover { background: #2a2418; }
.tier.recommended button { background: #c5460e; }
.tier.recommended button:hover { background: #9a3306; }
.footer {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px dashed #d4c8a0;
  font-family: 'Mukta', sans-serif;
  font-size: 15px;
  letter-spacing: 0.08em;
  text-align: center;
  color: #5a554d;
}
.success {
  margin-top: 20px;
  padding: 16px;
  background: rgba(90, 174, 90, 0.12);
  border-left: 4px solid #4a9a4a;
  font-family: 'Mukta', sans-serif;
  font-size: 18px;
  color: #0d0a06;
  display: none;
  border-radius: 0 4px 4px 0;
}
.success.show { display: block; }
</style>
<section class="shell">
  <div class="head">
    <div class="eyebrow">Mulya · Pricing · MMXXVI</div>
    <h3>Aapke maa-baap deserve karte hain yeh sab.</h3>
    <div class="tagline">Scam-shield · voice tutor · emergency SOS · family mirror. Sab ek jagah.</div>
  </div>
  <div class="tiers">
    <div class="tier" data-tier="free">
      <div class="label">Free</div>
      <div class="price">₹0</div>
      <div class="per">hamesha ke liye</div>
      <div class="desc">Big-tap UI · Phone-Tutor 10 tasks · Emergency SOS · 1 family contact.</div>
      <button data-go="free">Abhi start karein</button>
    </div>
    <div class="tier recommended" data-tier="pro_yearly">
      <div class="label">Pro · Yearly</div>
      <div class="price">₹499</div>
      <div class="per">per year</div>
      <div class="desc">Scam-Shield full · Phone-Tutor unlimited · 3 family contacts · UPI training wheels · Family-Mirror.</div>
      <button data-go="pro_yearly">Pro khareedein · ₹499</button>
    </div>
    <div class="tier" data-tier="family_yearly">
      <div class="label">Family · Yearly</div>
      <div class="price">₹2,999</div>
      <div class="per">per year · 5 elders</div>
      <div class="desc">5 bujurgo ke liye · Cross-elder rollup · SOS priority · Loneliness-ping sab ke liye.</div>
      <button data-go="family_yearly">Family khareedein · ₹2,999</button>
    </div>
    <div class="tier" data-tier="bundle_yearly">
      <div class="label">Empire Bundle</div>
      <div class="price">₹2,999</div>
      <div class="per">all 8 apps · 1 year</div>
      <div class="desc">Sarathi + Hisaab + Khazaana + Sehat + Wasiyat + sab. Ek family ke liye.</div>
      <button data-go="bundle_yearly">Bundle khareedein · ₹2,999</button>
    </div>
  </div>
  <div class="success" id="success"></div>
  <div class="footer">
    Razorpay live · 30-day money back · GST e-invoice · DPDP 2023 elder-care tier
  </div>
</section>
`;

export class SarathiPayCta extends HTMLElement {
  readonly #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const success = this.#shadow.getElementById('success') as HTMLDivElement;
    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-go]').forEach((b) => {
      b.addEventListener('click', async () => {
        const tier = b.dataset.go;
        if (tier === 'free' || typeof tier !== 'string') return;
        if (tier !== 'pro_yearly' && tier !== 'family_yearly' && tier !== 'bundle_yearly') return;
        try {
          await openCheckout(tier, {}, (pid) => {
            success.textContent = `Payment received · Razorpay id ${pid}. Sarathi Pro activation email is on the way.`;
            success.classList.add('show');
            localStorage.setItem('sarathi:tier', tier);
            localStorage.setItem('sarathi:payment_id', pid);
          });
        } catch (e) {
          success.textContent = `Checkout failed · ${e instanceof Error ? e.message : 'unknown'}. Email aditya@taxwalaai.com.`;
          success.classList.add('show');
        }
      });
    });
    void PRICING; // referenced for typecheck
  }
}

customElements.define('sarathi-pay-cta', SarathiPayCta);
